import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertCircle, RefreshCw } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { homePathForRole } from "../lib/homePath";
import { claimSessionTransition } from "../lib/sessionExpiry";
import { sessionAlreadyEnded } from "../lib/signOut";
import {
  CHECK_FAILED,
  CHECKING,
  CHECKING_NOTE,
  LEDE,
  LINK_SIGNS_IN,
  PHONE_SIGNED_IN_AS,
  SIGNOUT_FAILED,
  STAY,
  STAY_EXPLAINS,
  SWITCH,
  SWITCH_EXPLAINS,
  TITLE,
  TRY_AGAIN,
  WHAT_EACH,
} from "../lib/signInLinkOtherAccount";
import { BrandMark } from "../components/brand/BrandMark";
import { TAP_LINK_STYLE, tapLine } from "../components/kit/tapTarget";

/**
 * SignInLinkCallbackNew — issue #874, slice S2, the sign-in-link CALLBACK
 * half redrawn to `SignInLink.dc.html` (design-baseline-round-5). Lives
 * behind `newUi("signin")` (`App.js`, reusing S1's flag); `SignInLinkCallback
 * .jsx`, the shipped screen, is untouched and keeps serving
 * `/sign-in-link/callback` with the flag off.
 *
 * PRESENTATION ONLY for the ANONYMOUS exchange (the brief's own instruction,
 * word for word, S2) — `completeSignInLink`, the single-fire `ran` ref, the
 * POST-not-GET consume and the `replace` navigation are reused EXACTLY as
 * `SignInLinkCallback.jsx` has them for a caller with NO live session:
 * the exchange still fires exactly once, still guards React's development
 * double-mount the same way, and still lands the success toast
 * ("Signed in", D109) on the home page it navigates to.
 *
 * D144/D45 (issue #1022, round 7, follow-up to #891/#889) — WHAT CHANGED
 * HERE FROM S2. S2's own answer for a signed-in visitor was "go home,
 * unspent, no matter whose link it is" (#889's bug was this NOT happening
 * reliably). #891/D45 asks for more when the link is for a DIFFERENT
 * account: tell them whose it is (masked, D193.3) and let them choose,
 * rather than silently discarding the link on their behalf. So the old
 * user-truthy branch — which navigated home immediately, before anything
 * else could run — is gone, replaced by a peek: `POST /auth/sign-in-link
 * /peek` (#1015), which never spends the link, answers `same_account` and
 * `masked_email`, and the page decides from there:
 *
 *   - `same_account: true` — the link is theirs; go home unspent, exactly
 *     as before (D144 kept, just reached one read later).
 *   - `same_account: false` — render the `different-account` screen below.
 *   - the peek 401s — #1052 review F1: the session died BETWEEN the load
 *     and the peek (a real race, not hypothetical — see `runPeek`'s own
 *     catch). `sessionAlreadyEnded` reads that the SAME way every other
 *     sign-out path here does (a confirmed end, not an unknown), and the
 *     page runs the anonymous exchange directly rather than showing a
 *     "still signed in" message over a session that no longer exists.
 *   - the peek 400s (dead token) — `link-expired-or-used`, offering only
 *     "Stay signed in", never a sign-out: a dead link has nothing to spend.
 *   - the peek fails for any other reason (network/5xx) — `check-failed`:
 *     fail SAFE, never fall through to consuming. This is belt-and-braces
 *     — the server's own D191.4 refusal (`consume` 409
 *     `sign_in_link_other_account`) means this page being wrong could never
 *     actually spend a different account's link, only mis-render.
 *
 * "Sign out and continue" signs this device out ONLY (D193.4 — `logout`,
 * never `logoutEverywhere`) and only THEN spends the link, in that order:
 * the consume never runs before the sign-out has succeeded. A failed
 * sign-out leaves the link untouched and says so (`sign-out-failed`).
 * "Stay signed in" spends nothing and shows no toast (D109) — the same
 * destination D144 already sends an own-account link to.
 *
 * `ran` still guards the ONE-SHOT anonymous consume, byte-identical to
 * before. `peeked` is the equivalent one-shot guard for the peek's
 * AUTOMATIC firing only — `runPeek` is also called directly by the
 * check-failed screen's "Try again", which must be able to re-peek more
 * than once (the server's own docstring: `peek` is safe to call
 * repeatedly). `ran.current` is set at the START of "Sign out and
 * continue" too (before `logout` even runs), not only inside the
 * anonymous branch: `logout()` flips `user` to `false`, which would
 * otherwise re-trigger the SAME effect's anonymous branch — an automatic
 * SECOND consume of a token this handler is already in the middle of
 * spending once.
 *
 * WHY THE EFFECT ALSO WAITS ON `user`. `AuthContext` exposes auth as three
 * states through one field: `null` while the mount probe to `/auth/me` is
 * still in flight, `false` once it resolves to anonymous, and the user
 * object once it resolves signed-in. The pre-#890 effect keyed off `token`
 * alone, so it fired the consume POST on every mount regardless of `user`
 * — that gap is exactly what #889 exploited, and the peek path above never
 * spends anything either, so both branches stay safe under the same wait.
 *
 * DESIGNERS' NOTES ARE NOT COPY (S1's B1, checked again for this slice):
 * `SignInLink.dc.html`'s dashed-border captions ("When the link has expired,
 * or was already used", "When the link carries no token at all", "Opened
 * while already signed in") and its trailing rationale paragraphs ("It
 * works the moment it lands...", "Expired, already spent, or not a link
 * this app minted...", "The same screen, one line different...") are all
 * checked against every state below and NONE of them render. "Where the
 * link lands" is the ONE exception — a plain sibling of the drawing's own
 * `data-dc-note="commentary"` paragraph, not inside it, so it IS real UI
 * (a conformance fix-round correction; see its own render site below).
 * Everything else solid — the spinner heading, the "This link didn't work"
 * headline/body pair, the two buttons — is real UI too. Same rule checked
 * again for `SignInLinkOtherAccount.dc.html` (round 7, #1022): every
 * `data-dc-note="commentary"` block and every dashed-border state caption
 * on that artboard is excluded below; only the solid-cornered content is
 * real UI.
 *
 * NO-TOKEN STATE: the drawing's own commentary reads "The same screen, one
 * line different" for the missing-token case — same headline, same two
 * buttons as expired/used, only the body line differs. Both share the ONE
 * `sil-cb-error` testid, exactly as the shipped screen already does.
 */
// The kit's tap-link box without its symmetric `marginBlock`: the exit link sets its own margins
// (it grows downward only, see the link below).
const { marginBlock: _symmetric, ...EXIT_LINK_BOX } = TAP_LINK_STYLE;

export default function SignInLinkCallbackNew() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { user, completeSignInLink, logout } = useAuth();
  const nav = useNavigate();
  // Guards the ONE-SHOT anonymous consume (#889/#890 port) — see the
  // header. Also claimed by "Sign out and continue" before it runs, so
  // `logout()` flipping `user` to `false` can never re-trigger this same
  // effect's anonymous branch mid-handler.
  const ran = useRef(false);
  // Guards the peek's AUTOMATIC first fire only. "Try again"
  // (check-failed) calls `runPeek` directly, unguarded — the server's own
  // peek route is safe to call more than once.
  const peeked = useRef(false);
  // "working" until the exchange resolves; "error" if the token was
  // invalid/expired/used. Success navigates away, so there is no "success"
  // terminal state to render. Also the state shown while auth itself is
  // still loading (`user === null`) — indistinguishable from the exchange
  // being mid-flight, which is the point: nothing below fires until we know.
  const [state, setState] = useState(token ? "working" : "missing");
  // The link's own masked address (D193.3), set once the peek finds a
  // different account. Never the full address — only `maskEmail`'s form,
  // exactly as the server sends it.
  const [maskedEmail, setMaskedEmail] = useState("");

  // D193.3 — reads whose link this is without ever spending it. Never
  // calls `completeSignInLink`. Called once automatically (via the effect
  // below, `peeked`-guarded) and again on demand from "Try again".
  const runPeek = async () => {
    setState("checking");
    try {
      const { data } = await api.post("/auth/sign-in-link/peek", { token });
      if (data.same_account) {
        // The link is theirs — D144, just reached one read later than the
        // pre-#1022 shape. Never spent here — this branch never calls
        // completeSignInLink (the catch's own F1 branch below does, but
        // only once the session is server-confirmed already gone).
        nav(homePathForRole(user.role), { replace: true });
        return;
      }
      setMaskedEmail(data.masked_email);
      setState("different-account");
    } catch (e) {
      // #1052 review F1 — a 401 here means the session died BETWEEN the
      // load and this request (a race `/auth/me`'s own mount probe cannot
      // see: it already resolved to a user, and the peek is a later,
      // separate round trip). `/sign-in-link/callback` is a PUBLIC_PATHS
      // route, so the shared 401 interceptor (`sessionExpiry.js`) never
      // touches this request either — nothing else was going to catch it.
      // `sessionAlreadyEnded` (`lib/signOut.js`) is the SAME read every
      // other sign-out path in this app already trusts: a 401 IS a
      // confirmed "this cookie authenticates nobody", not an unknown. With
      // no live session left, there is nothing D191.4 needs protected —
      // the server's own consume re-checks the live session fresh at
      // consume time and finds none for this caller either, so running the
      // SAME anonymous exchange the effect's own `user`-falsy branch uses
      // is exactly as server-safe as it always is, never a bypass. `ran`
      // is claimed first, the same guard that branch uses, so the effect
      // itself can never also fire it once `user` eventually settles.
      if (sessionAlreadyEnded(e)) {
        ran.current = true;
        const signedIn = await completeSignInLink(token);
        if (signedIn) {
          toast.success("Signed in");
          nav(homePathForRole(signedIn.role), { replace: true });
        } else {
          setState("error");
        }
        return;
      }
      // 400 (SIGN_IN_LINK_INVALID) — the token is dead. Anything else
      // (network, timeout, 5xx) is a check that could not be made at all,
      // never the same thing as a dead link: fail SAFE, never fall through
      // to offering a sign-out for a link this page never actually
      // confirmed is even for someone else.
      if (e.response?.status === 400) {
        setState("link-expired-or-used");
      } else {
        setState("check-failed");
      }
    }
  };

  useEffect(() => {
    if (!token || ran.current) return;
    // Auth hasn't resolved yet (hard load) — wait. Re-runs when `user`
    // settles, via the dep below. (#889/#890 port)
    if (user === null) return;
    if (user) {
      // Already signed in, for this account or another's. D144/D45
      // (#1022): find out which BEFORE deciding anything — the peek never
      // spends the link, so this is always safe to run.
      if (peeked.current) return;
      peeked.current = true;
      runPeek();
      return;
    }
    ran.current = true;
    (async () => {
      const signedIn = await completeSignInLink(token);
      if (signedIn) {
        toast.success("Signed in");
        nav(homePathForRole(signedIn.role), { replace: true });
      } else {
        setState("error");
      }
    })();
    // completeSignInLink/nav are stable enough for this one-shot effect; the
    // ref guard is what actually enforces once, not the dep list. `user` IS
    // a real dependency, unlike those two — see the D144 note above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, user]);

  // D109 — no toast; nothing changed. Same destination a same-account link
  // already goes to.
  const handleStay = () => {
    nav(homePathForRole(user.role), { replace: true });
  };

  // D193.4 — this device only (`logout`, never `logoutEverywhere`), and
  // only THEN the consume: order matters, the server also refuses a
  // consume ahead of a confirmed sign-out (D191.4).
  const handleSwitch = async () => {
    // Claimed before `logout()` runs — see the header note on `ran`.
    ran.current = true;
    setState("continuing");
    // #436 item 5 — claimed BEFORE the request, same as `signOut.js`/
    // `ForcePasswordChange.jsx`: the moment the server deletes the session
    // row, anything this page still has in flight comes back 401, and the
    // shared interceptor would otherwise read the first one as an
    // unexpected expiry and bounce to /login?session=expired — exactly the
    // sentence a "Sign out and continue" press must not produce.
    //
    // #1052 review F2 — released once `completeSignInLink` RESOLVES,
    // success or failure, not left claimed the way `ForcePasswordChange
    // .jsx`'s own `doSignOut` leaves it. That precedent holds THERE
    // because a successful sign-out lands on `/login` — a page that makes
    // no further authenticated requests for the interceptor to ever need
    // to catch. Here a successful switch lands the visitor on B's OWN
    // authenticated pages (dashboard, onboarding, …) and they go on using
    // the app as B, client-side, no reload — exactly the shape #396's
    // expiry bounce exists to cover. Leaving the latch claimed through
    // that would silently disable "your session ended" detection for B,
    // for the rest of the tab's life, the moment B's OWN session later
    // expires or is revoked (review probe P5: killed B's session, SPA-
    // navigated, no bounce to /login?session=expired — confirmed dead
    // without this release). A FAILED consume (the `else` below) also
    // releases: the sign-out already succeeded by that point, so the
    // session transition this latch was protecting is over either way.
    const releaseSessionTransition = claimSessionTransition();
    const result = await logout();
    if (!result?.ok) {
      releaseSessionTransition();
      setState("sign-out-failed");
      return;
    }
    const signedIn = await completeSignInLink(token);
    releaseSessionTransition();
    if (signedIn) {
      toast.success("Signed in");
      nav(homePathForRole(signedIn.role), { replace: true });
    } else {
      // The built signed-OUT "This link didn't work" — the sign-out has
      // already happened, so this is the same dead-link screen an
      // anonymous visitor gets, not the signed-in one above.
      setState("error");
    }
  };

  // The two buttons share one look each across every state that offers
  // them; `primary` is the one difference `link-expired-or-used` needs
  // (the only action on that screen, drawn as a full solid button rather
  // than the usual underlined secondary).
  const renderStay = (primary) => (
    <button
      type="button"
      data-testid="sil-cb-stay"
      onClick={handleStay}
      style={
        primary
          ? {
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "var(--nw-size-control)",
              background: "var(--nw-navy)",
              color: "#FFFFFF",
              fontWeight: 700,
              fontSize: 15,
              borderRadius: "var(--nw-radius-control)",
              border: "none",
              cursor: "pointer",
            }
          : {
              alignSelf: "flex-start",
              fontSize: 13,
              fontWeight: 600,
              color: "var(--nw-navy)",
              textDecoration: "underline",
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
            }
      }
    >
      {STAY}
    </button>
  );

  const renderSwitch = () => (
    <button
      type="button"
      data-testid="sil-cb-switch"
      onClick={handleSwitch}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        minHeight: "var(--nw-size-control)",
        background: "var(--nw-navy)",
        color: "#FFFFFF",
        fontWeight: 700,
        fontSize: 15,
        borderRadius: "var(--nw-radius-control)",
        border: "none",
        cursor: "pointer",
      }}
    >
      {SWITCH}
    </button>
  );

  return (
    <div style={{ minHeight: "100vh", background: "var(--nw-page)" }}>
      {/* Round 5 conformance (token-check) — declares its own ink token
          rather than inheriting the shell's `body` colour, same rule
          FrontDoorNew/SignInNew's own markers state. */}
      <span data-testid="sign-in-link-callback-new-screen" className="sr-only" style={{ color: "var(--nw-ink)" }} aria-hidden="true" />

      <div
        data-testid="sign-in-link-callback-page"
        className="nw-column"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--nw-space-stack)",
          padding: "var(--nw-space-page)",
          // Round 5 conformance (token-check) — the same fix
          // SignInLinkRequestNew.jsx's own sign-in-link-page needed: this
          // IS the drawing's own data-dc-id, sampled directly.
          color: "var(--nw-ink)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--nw-ink)" }}>
          <BrandMark size={24} />
          <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: 16, color: "var(--nw-navy)" }}>
            Compass
          </span>
        </div>

        {/* "Where the link lands" is a plain sibling of the drawing's own
            data-dc-note="commentary" paragraph, not inside it — real UI,
            the section-wide label for every state below, not a note. */}
        <div className="nw-section" style={{ color: "var(--nw-navy)" }}>
          Where the link lands
        </div>

        {(state === "working" || state === "continuing") && (
          <div
            data-testid="sil-cb-working"
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 8,
              padding: "18px 14px",
              background: "var(--nw-page)",
              border: "1px solid var(--nw-line)",
              borderRadius: "var(--nw-radius-card)",
            }}
          >
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="var(--nw-ink-muted)" strokeWidth="2" strokeLinecap="round" className="animate-spin" aria-hidden="true">
              <path d="M21 12a9 9 0 1 1-6.2-8.6" />
            </svg>
            <div className="nw-title" style={{ fontSize: 19 }}>
              Signing you in…
            </div>
            <div className="nw-prose" style={{ textAlign: "center", color: "var(--nw-ink-muted)" }}>
              One moment while we verify your link.
            </div>
          </div>
        )}

        {state === "checking" && (
          <div
            data-testid="sil-cb-checking"
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 8,
              padding: "18px 14px",
              background: "var(--nw-page)",
              border: "1px solid var(--nw-line)",
              borderRadius: "var(--nw-radius-card)",
            }}
          >
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="var(--nw-ink-muted)" strokeWidth="2" strokeLinecap="round" className="animate-spin" aria-hidden="true">
              <path d="M21 12a9 9 0 1 1-6.2-8.6" />
            </svg>
            <div className="nw-title" style={{ fontSize: 19 }}>
              {CHECKING}
            </div>
            <div className="nw-prose" style={{ textAlign: "center", color: "var(--nw-ink-muted)" }}>
              {CHECKING_NOTE}
            </div>
          </div>
        )}

        {state === "different-account" && (
          <div data-testid="sil-cb-other-account" style={{ display: "flex", flexDirection: "column", gap: "var(--nw-space-stack)" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div className="nw-title">{TITLE}</div>
              <div className="nw-lede">{LEDE}</div>
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 10,
                padding: "var(--nw-space-card)",
                background: "var(--nw-surface)",
                border: "1px solid var(--nw-line)",
                borderRadius: "var(--nw-radius-card)",
              }}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <span className="nw-help" style={{ lineHeight: 1 }}>{LINK_SIGNS_IN}</span>
                <span data-testid="sil-cb-link-account" style={{ fontSize: 15, fontWeight: 700, color: "var(--nw-ink)", overflowWrap: "anywhere" }}>
                  {maskedEmail}
                </span>
              </div>
              <div style={{ height: 1, background: "var(--nw-line-faint)" }} />
              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <span className="nw-help" style={{ lineHeight: 1 }}>{PHONE_SIGNED_IN_AS}</span>
                <span data-testid="sil-cb-signed-in-as" style={{ fontSize: 15, fontWeight: 700, color: "var(--nw-ink)", overflowWrap: "anywhere" }}>
                  {user.email}
                </span>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 6,
                padding: "var(--nw-space-card)",
                background: "var(--nw-surface)",
                border: "1px solid var(--nw-line)",
                borderRadius: "var(--nw-radius-card)",
              }}
            >
              <div className="nw-section">{WHAT_EACH}</div>
              {/* Bolds only the lead-in verb phrase, the artboard's own
                  split — `SWITCH_EXPLAINS`/`STAY_EXPLAINS` each start with
                  their own SWITCH/STAY constant verbatim (pinned in
                  signInLinkOtherAccount.test.js), so slicing at that
                  length reconstructs the split without stating the
                  sentence twice. */}
              <div style={{ fontSize: 14, lineHeight: 1.5, color: "var(--nw-ink)", overflowWrap: "anywhere" }}>
                <strong>{SWITCH}</strong>
                {SWITCH_EXPLAINS(user.email, maskedEmail).slice(SWITCH.length)}
              </div>
              <div style={{ fontSize: 14, lineHeight: 1.5, color: "var(--nw-ink)", overflowWrap: "anywhere" }}>
                <strong>{STAY}</strong>
                {STAY_EXPLAINS.slice(STAY.length)}
              </div>
            </div>

            {renderSwitch()}
            {renderStay(false)}
          </div>
        )}

        {state === "link-expired-or-used" && (
          <div
            data-testid="sil-cb-error"
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 10,
              padding: "12px 14px",
              background: "var(--nw-surface)",
              border: "1px solid var(--nw-line)",
              borderRadius: "var(--nw-radius-card)",
            }}
          >
            <div className="nw-title" style={{ fontSize: 19 }}>
              This link didn't work
            </div>
            <div className="nw-prose" style={{ color: "var(--nw-ink-muted)" }}>
              Sign-in links expire after a short time and can only be used once.
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                padding: "var(--nw-space-card)",
                background: "var(--nw-surface)",
                border: "1px solid var(--nw-line)",
                borderRadius: "var(--nw-radius-card)",
              }}
            >
              <span className="nw-help" style={{ lineHeight: 1 }}>{PHONE_SIGNED_IN_AS}</span>
              <span data-testid="sil-cb-signed-in-as" style={{ fontSize: 15, fontWeight: 700, color: "var(--nw-ink)", overflowWrap: "anywhere" }}>
                {user.email}
              </span>
            </div>
            {/* A dead link names no one that would need signing out of —
                Stay signed in is the only choice this state offers. */}
            {renderStay(true)}
          </div>
        )}

        {state === "check-failed" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div
              role="alert"
              data-testid="sil-cb-check-failed"
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "var(--nw-space-inline)",
                padding: "10px 12px",
                background: "var(--nw-line-faint)",
                borderLeft: "3px solid var(--nw-alert)",
                borderRadius: "var(--nw-radius-control)",
              }}
            >
              <AlertCircle size={16} style={{ color: "var(--nw-alert)", flexShrink: 0, marginTop: 1 }} aria-hidden="true" />
              <div style={{ fontSize: 13, lineHeight: 1.5, color: "var(--nw-ink)" }}>{CHECK_FAILED(user.email)}</div>
            </div>
            <button
              type="button"
              data-testid="sil-cb-check-retry"
              onClick={runPeek}
              style={{
                alignSelf: "flex-start",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                minHeight: "var(--nw-size-chip)",
                padding: "0 14px",
                border: "1px solid var(--nw-line-strong)",
                borderRadius: "var(--nw-radius-control)",
                fontSize: 14,
                fontWeight: 600,
                color: "var(--nw-navy)",
                background: "var(--nw-surface)",
                cursor: "pointer",
              }}
            >
              <RefreshCw size={14} aria-hidden="true" />
              {TRY_AGAIN}
            </button>
            {renderStay(false)}
          </div>
        )}

        {state === "sign-out-failed" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div
              role="alert"
              data-testid="sil-cb-signout-error"
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "var(--nw-space-inline)",
                padding: "10px 12px",
                background: "var(--nw-line-faint)",
                borderLeft: "3px solid var(--nw-alert)",
                borderRadius: "var(--nw-radius-control)",
              }}
            >
              <AlertCircle size={16} style={{ color: "var(--nw-alert)", flexShrink: 0, marginTop: 1 }} aria-hidden="true" />
              <div style={{ fontSize: 13, lineHeight: 1.5, color: "var(--nw-ink)" }}>{SIGNOUT_FAILED(user.email)}</div>
            </div>
            {renderSwitch()}
            {renderStay(false)}
          </div>
        )}

        {(state === "error" || state === "missing") && (
          <div
            data-testid="sil-cb-error"
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 10,
              padding: "12px 14px",
              background: "var(--nw-surface)",
              border: "1px solid var(--nw-line)",
              borderRadius: "var(--nw-radius-card)",
            }}
          >
            <div className="nw-title" style={{ fontSize: 19 }}>
              This link didn't work
            </div>
            <div className="nw-prose" style={{ color: "var(--nw-ink-muted)" }}>
              {state === "missing"
                ? "This link is missing its sign-in token."
                : "Sign-in links expire after a short time and can only be used once. Request a fresh one to continue."}
            </div>
            <Link to="/sign-in-link" data-testid="sil-cb-request-new" style={{ textDecoration: "none" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: "var(--nw-size-control)",
                  background: "var(--nw-navy)",
                  color: "#FFFFFF",
                  fontWeight: 700,
                  fontSize: 15,
                  borderRadius: "var(--nw-radius-control)",
                }}
              >
                Email me a new link
              </div>
            </Link>
            <div style={{ display: "flex", justifyContent: "center" }}>
              {/* R4 (review round 2) — a literal, not the imported
                  SIGN_IN_PATH constant: this file's only /login reference,
                  same reasoning as SignInLinkRequestNew.jsx's own sil-back
                  link. */}
              <Link
                to="/login"
                data-testid="sil-cb-password"
                // #1394 (D221 G4, D354): the page's exit, a 44px tap box around the unchanged label.
                // The card stacks it 10px under "Email me a new link", so the box may not reach up
                // past that gap (it would cover the button's last pixels): it grows downward instead.
                // The label keeps its place (10px padding above it), and the margin box is still
                // one line ((-10px) + 44px + (1lh - 34px) = 1lh).
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: "var(--nw-navy)",
                  textDecoration: "none",
                  ...EXIT_LINK_BOX,
                  alignItems: "flex-start",
                  paddingTop: 10,
                  marginTop: -10,
                  marginBottom: `calc(${tapLine()} - 34px)`,
                }}
              >
                Sign in with a password instead
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
